/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — track changes.

   The View menu's "Track changes" (BlockEditor T3 / handleShowDiff):
   a read-only, Word-style inline diff of the open draft against the
   PUBLISHED master, coloured per block with word-level highlighting on
   modifications. It reuses `buildInlineDiff` / `diffWords` from
   specService — the same brain the old editor used, so the two views
   report identical differences.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { InlineDiffBlock } from '../services/specService';
import EmptyState from '../components/EmptyState';
import { InfoHint } from './InfoHint';

const KIND_LABEL: Record<string, string> = {
  added: 'Added',
  removed: 'Removed',
  modified: 'Changed',
  unchanged: '',
};

const DiffIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M5 3v12a3 3 0 0 0 3 3h8M16 6l3-3-3-3M8 21V9" />
  </svg>
);

export function TrackChangesView({
  blocks,
  publishedVersion,
}: {
  blocks: InlineDiffBlock[];
  /** The published version this draft is measured against. */
  publishedVersion: number;
}) {
  const changed = React.useMemo(
    () => blocks.filter((b) => b.kind !== 'unchanged').length,
    [blocks]
  );

  return (
    <div className="dsg-canvas sps-canvas">
      <div className="sps-sheet sps-sheet-diff" role="region" aria-label="Changes since publish">
        <div className="sps-diff-head">
          <h2 className="sps-diff-title">
            Changes in this draft vs. published v{publishedVersion}
          </h2>
          <InfoHint
            label="Track changes"
            text="What this draft changes compared with the published version."
          />
        </div>
        <div className="sps-diff-bar" role="status">
          {changed === 0
            ? 'No changes since the published version'
            : `${changed} block${changed === 1 ? '' : 's'} changed since the published version`}
        </div>

        {blocks.length === 0 ? (
          <EmptyState
            icon={DiffIcon}
            title="Nothing to compare yet"
            subtitle="This master has no content on either side."
          />
        ) : (
          blocks.map((b, i) => (
            <p
              key={`${b.id}-${i}`}
              data-diff-id={b.id}
              className={`dsg-block sps-diff-row sps-diff-${b.kind} sps-diff-l${Math.min(b.level || 1, 9)}`}
            >
              {KIND_LABEL[b.kind] && (
                <span className="sps-diff-tag" aria-hidden="true">
                  {KIND_LABEL[b.kind]}
                </span>
              )}
              {b.wordDiff ? (
                b.wordDiff.map((t, j) => (
                  <span key={j} className={`sps-tok sps-tok-${t.op}`}>
                    {t.value}
                  </span>
                ))
              ) : (
                <span className="sps-tok sps-tok-keep">
                  {b.kind === 'removed' ? b.oldText : b.newText || b.oldText}
                </span>
              )}
            </p>
          ))
        )}
      </div>
    </div>
  );
}
